import { NavLink } from "react-router-dom";

import type { ControlId } from "../lib/controls";
import { DEFAULT_LABELS } from "../lib/controls";
import { useControls } from "../lib/useControls";
import {
  IconAdmin,
  IconCall,
  IconConsole,
  IconDashboard,
  IconHistory,
  IconScripts,
  IconSettings,
  IconSip,
  IconStats,
} from "./Icons";
import { cx } from "./ui";

const STAFF_PRIMARY: Array<{
  to: string;
  label: string;
  icon: typeof IconConsole;
  end?: boolean;
  id: ControlId | null;
}> = [
  { to: "/console", label: "Dial", icon: IconConsole, id: null },
  { to: "/call", label: "New call", icon: IconCall, id: "new_call" },
  { to: "/scripts", label: "Scripts", icon: IconScripts, id: "scripts" },
  { to: "/history", label: "History", icon: IconHistory, id: "history" },
  { to: "/stats", label: "Stats", icon: IconStats, id: null },
  { to: "/sip", label: "SIP", icon: IconSip, id: "sip" },
  { to: "/settings", label: "Settings", icon: IconSettings, id: "settings" },
];

/** Bottom extras. Always shown on this single-login host (do not hide Admin). */
const SUPERUSER: Array<{
  to: string;
  label: string;
  icon: typeof IconConsole;
}> = [
  { to: "/orchestrator", label: "Voice ops", icon: IconDashboard },
  { to: "/admin", label: "Admin", icon: IconAdmin },
];

function NavItem({
  to,
  label,
  icon: Icon,
  end,
}: {
  to: string;
  label: string;
  icon: typeof IconConsole;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      title={label}
      className={({ isActive }) =>
        cx(
          "flex h-11 w-11 items-center justify-center rounded-2xl transition",
          isActive
            ? "bg-teal/15 text-teal shadow-[0_0_18px_rgb(46_230_214_/_0.18)]"
            : "text-mute hover:bg-white/5 hover:text-ink",
        )
      }
    >
      <Icon />
      <span className="sr-only">{label}</span>
    </NavLink>
  );
}

/**
 * Thin icon rail. Staff: Dial / New call / Scripts / History / SIP / Settings. Admin extra.
 */
export function Sidebar() {
  const { config } = useControls();
  const labelFor = (id: ControlId | null, fallback: string) =>
    id ? config.labels[id].trim() || DEFAULT_LABELS[id] : fallback;

  return (
    <aside className="flex w-[72px] shrink-0 flex-col items-center overflow-y-auto border-r border-teal/10 bg-navy/80 py-4">
      <NavLink
        to="/console"
        title="CDIALER"
        className="mb-6 flex h-11 w-11 items-center justify-center rounded-2xl border border-border bg-teal/15 font-semibold tracking-tight text-teal"
      >
        C
      </NavLink>
      <nav className="flex flex-1 flex-col items-center gap-1.5">
        {STAFF_PRIMARY.map((item) => (
          <NavItem
            key={item.to}
            to={item.to}
            label={labelFor(item.id, item.label)}
            icon={item.icon}
            end={item.end}
          />
        ))}
      </nav>
      <nav className="flex flex-col items-center gap-1.5">
        {SUPERUSER.map((item) => (
          <NavItem key={item.to} to={item.to} label={item.label} icon={item.icon} />
        ))}
      </nav>
    </aside>
  );
}
